import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTabs } from "@/contexts/TabsContext";
import { isVoucherFormOpen, clearVoucherFormFlag } from "@/lib/voucherGuard";
import { api } from "@/lib/api";
import TabBar from "@/components/TabBar";
import TabContent from "@/components/TabContent";
import { NAV_SECTIONS, canSeeModule } from "@/lib/permissions";
import { LogOut, Menu, X, ChevronDown, Search, KeyRound } from "lucide-react";
import { useState, useEffect } from "react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import FYSwitcher from "@/components/FYSwitcher";
import BranchSwitcher from "@/components/BranchSwitcher";
import NotificationBell from "@/components/NotificationBell";
import { Toaster } from "sonner";

const SECTIONS = NAV_SECTIONS;


export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const { openTab, activePath } = useTabs();
  const [open, setOpen] = useState(false);
  // Sections start collapsed by default. Persist across reloads so user choice sticks.
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidebar.expanded") || "{}"); } catch { return {}; }
  });
  const [company, setCompany] = useState(null);
  const [activeFY, setActiveFY] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  // Jul-2026 — sidebar module search: filters nav items across every
  // section (matches item label or section label, case-insensitive).
  const [navSearch, setNavSearch] = useState("");
  const navQ = navSearch.trim().toLowerCase();

  useEffect(() => { localStorage.setItem("sidebar.expanded", JSON.stringify(expanded)); }, [expanded]);
  useEffect(() => {
    const loadCompany = () => api.get("/company-settings").then(({ data }) => setCompany(data)).catch(() => {});
    loadCompany();
    api.get("/financial-years/active").then(({ data }) => setActiveFY(data)).catch(() => {});
    // Aug-2026 — refresh the sidebar logo immediately after Company Setup
    // uploads a new one (no re-login / hard refresh needed).
    window.addEventListener("company-settings-updated", loadCompany);
    return () => window.removeEventListener("company-settings-updated", loadCompany);
  }, []);

  function toggleSection(label) {
    setExpanded(prev => ({ ...prev, [label]: !prev[label] }));
  }

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  function canSee(item) {
    // Sprint 2026-Jun — STRICT permission model ("No access means nothing
    // to show"). Single source of truth lives in lib/permissions.js and is
    // shared with the deep-link route guard in TabContent.
    return canSeeModule(item, hasPerm, user);
  }

  return (
    <div className="min-h-screen flex bg-[#F7F7F8]">
      {/* Sprint 2026-Feb — Global Toaster mounted once at the layout level.
          Every voucher form (Return Voucher, Issue Voucher, Payment Receipt,
          etc.) lives inside its own Tab; page-local <Toaster> mounts were
          being swallowed the moment the user was on a *different* tab than
          the one that fired the toast (no Toaster in that subtree = the
          `sonner` message is silently dropped). Mounting a single global
          Toaster here guarantees toast.success / toast.error always render
          regardless of which tab is currently visible. Individual page
          <Toaster> mounts are harmless duplicates. */}
      <Toaster richColors position="top-right" />
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-[56] bg-[#1A1D21] text-white px-4 py-3 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#F97316] flex items-center justify-center font-black text-xs">R</div>
          <span className="font-display font-black tracking-tight text-base">RADIANTS PRIMEWORK</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-1">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      {/* Sidebar — Light Navy theme, premium ERP look */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky lg:top-0 z-[60] lg:z-30 inset-y-0 left-0 w-64 h-screen bg-[#0B1F3A] text-slate-200 flex flex-col transition-transform duration-200 border-r border-[#0B1F3A] shadow-md ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="hidden lg:flex flex-col items-center gap-3 px-4 pt-5 pb-5 border-b border-white/10">
          {company?.logo_url ? (
            <div className="bg-white rounded-xl p-3 shadow-sm">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo_url}`} alt="Logo"
                className="w-44 h-16 object-contain" data-testid="sidebar-logo" />
            </div>
          ) : (
            <div className="w-48 h-20 flex items-center justify-center font-black text-white text-3xl tracking-wide">RADIANTS</div>
          )}
          <div className="text-center">
            <div className="text-[16px] uppercase tracking-[0.32em] text-[#F97316] font-black" data-testid="sidebar-primework">PrimeWork</div>
            <div className="mt-1 text-[11px] italic text-slate-300 font-serif tracking-wide" data-testid="sidebar-tagline">
              "Radiants is my Pride"
            </div>
          </div>
        </div>

        <div className="px-3 pt-3 pb-2 border-b border-white/10 mt-14 lg:mt-0">
          {/* Mobile-only: user + logout pinned to the TOP of the panel —
              the bottom block sits off-screen on small phones. */}
          <div className="lg:hidden flex items-center justify-between gap-2 pb-2 mb-2 border-b border-white/10">
            <div className="min-w-0">
              <div className="text-xs text-slate-200 truncate font-bold" data-testid="mobile-user-email">{user?.email}</div>
              <span className="text-[9px] uppercase tracking-widest bg-[#F97316] text-white px-1.5 py-0.5 font-bold rounded inline-block mt-0.5">{user?.role}</span>
            </div>
            <div className="shrink-0 flex items-center gap-1.5">
              <button data-testid="mobile-change-password-btn" onClick={() => { setPwOpen(true); setOpen(false); }}
                title="Change my password"
                className="flex items-center justify-center text-white bg-white/10 hover:bg-[#F97316] border border-white/20 p-2 rounded transition-colors">
                <KeyRound size={14} />
              </button>
              <button data-testid="mobile-logout-btn" onClick={handleLogout}
                className="flex items-center gap-1.5 text-xs font-bold text-white bg-white/10 hover:bg-[#F97316] border border-white/20 px-3 py-2 rounded transition-colors">
                <LogOut size={14} /> Logout
              </button>
            </div>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              data-testid="sidebar-search-input"
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder="Search modules…"
              className="w-full bg-white/10 border border-white/15 focus:border-[#F97316] text-slate-100 placeholder:text-slate-400 text-[12px] rounded pl-8 pr-7 py-1.5 focus:outline-none"
            />
            {navSearch && (
              <button type="button" data-testid="sidebar-search-clear"
                onClick={() => setNavSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {(() => {
            const rendered = SECTIONS.map((section) => {
              const permitted = section.items.filter(canSee);
              const visible = navQ
                ? (section.label.toLowerCase().includes(navQ)
                    ? permitted
                    : permitted.filter(i => i.label.toLowerCase().includes(navQ)))
                : permitted;
              if (visible.length === 0) return null;
              const hasActive = visible.some(i => activePath === i.to);
              const isOpen = navQ ? true : (expanded[section.label] || hasActive);
              return (
                <div key={section.label} className="mb-1">
                  <button type="button" onClick={() => toggleSection(section.label)}
                    data-testid={`sidebar-section-${section.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="w-full flex items-center justify-between gap-1.5 px-4 py-2.5 text-[11px] uppercase tracking-[0.1em] text-slate-400 hover:text-white font-black">
                    <span className="truncate whitespace-nowrap">{section.label}</span>
                    <ChevronDown size={13} className={`flex-shrink-0 transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} />
                  </button>
                  {isOpen && visible.map((n) => {
                    const Icon = n.icon;
                    const isActive = activePath === n.to;
                    return (
                      <button
                        key={n.to}
                        type="button"
                        onClick={(e) => {
                          if (isVoucherFormOpen()) {
                            if (!window.confirm("You have an unsaved voucher open. Discard it and leave this page?")) {
                              e.preventDefault();
                              return;
                            }
                            clearVoucherFormFlag();
                          }
                          openTab(n.to);
                          setOpen(false);
                          setNavSearch("");
                        }}
                        data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`sidebar-link w-full flex items-center gap-3 px-7 py-2 text-[13px] text-left transition-colors ${isActive ? "active text-white font-bold bg-white/10 border-l-2 border-[#F97316]" : "text-slate-300 hover:text-white hover:bg-white/5"}`}
                      >
                        <Icon size={15} strokeWidth={1.5} />
                        <span>{n.label}</span>
                      </button>
                    );
                  })}
                </div>
              );
            });
            if (navQ && rendered.every(r => r === null)) {
              return (
                <div className="px-4 py-6 text-center text-[12px] text-slate-400" data-testid="sidebar-search-empty">
                  No modules match “{navSearch.trim()}”.
                </div>
              );
            }
            return rendered;
          })()}
        </nav>

        <div className="hidden lg:block border-t border-white/10 px-6 py-3 bg-black/20">
          <div className="text-xs text-slate-200 mb-2 truncate font-bold" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest bg-[#F97316] text-white px-2 py-0.5 font-bold rounded">{user?.role}</span>
            <div className="flex items-center gap-3">
              <button data-testid="change-password-btn" onClick={() => setPwOpen(true)}
                title="Change my password"
                className="text-slate-300 hover:text-[#F97316] text-xs flex items-center gap-1">
                <KeyRound size={13} />
              </button>
              <button data-testid="logout-btn" onClick={handleLogout} className="text-slate-300 hover:text-[#F97316] text-xs flex items-center gap-1">
                <LogOut size={13} /> Logout
              </button>
            </div>
          </div>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-[55]" onClick={() => setOpen(false)} />}

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      <main className="flex-1 min-w-0 pt-14 lg:pt-0 flex flex-col">
        <div className="hidden lg:flex items-center justify-between gap-4 px-10 py-3 border-b border-slate-200 bg-white">
          {/* Welcome lock-up — sits in the top patch so it's always visible
              (was inside the Dashboard hero earlier). Greeting in deep navy,
              FY pill in brand orange for instant scanability. */}
          <div className="flex items-baseline gap-3 min-w-0" data-testid="topbar-welcome">
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Welcome to</span>
            <span className="font-display text-base font-black text-[#0B1F3A] truncate">
              {company?.company_name || "Radiants PrimeWork"}
            </span>
            <span className="hidden xl:inline text-slate-300">·</span>
            <span className="hidden xl:inline text-[11px] uppercase tracking-wider text-slate-500 font-bold">Current FY</span>
            <span className="text-[11px] font-black px-2 py-0.5 rounded bg-[#F97316]/10 text-[#F97316] tracking-wider">
              {activeFY?.code || "—"}
            </span>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <NotificationBell />
            <BranchSwitcher />
            <FYSwitcher />
          </div>
        </div>
        <TabBar />
        <TabContent />
      </main>
    </div>
  );
}
